import React, { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { MessageCircle, Loader2, CheckCircle2, X, Copy } from 'lucide-react';
import { toast } from 'sonner';

export const WhatsAppConnectCard = ({ standalone = false, onDone }) => {
  const [status, setStatus] = useState(null);
  const [link, setLink] = useState(null);
  const [starting, setStarting] = useState(false);
  const [dismissed, setDismissed] = useState(() => !standalone && localStorage.getItem('cmx_wa_dismiss') === '1');
  const pollRef = useRef(null);

  useEffect(() => {
    api.get('/api/whatsapp/status').then((r) => setStatus(r.data)).catch(() => setStatus({ enabled: false }));
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    if (!link || status?.connected) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get('/api/whatsapp/status');
        if (data.connected) {
          setStatus(data);
          clearInterval(pollRef.current);
          toast.success('WhatsApp connected! 🎉');
          onDone?.();
        }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [link, status?.connected, onDone]);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post('/api/whatsapp/verify/start', { consent: true, channel: 'dashboard_card' });
      setLink(data);
    } catch { toast.error('Could not start verification — try again'); }
    setStarting(false);
  };

  if (!status || !status.enabled || dismissed) return null;
  if (status.connected && !standalone) return null;

  if (status.connected) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="wa-connected-badge">
        <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-2" />
        <p className="font-bold text-emerald-900">WhatsApp connected!</p>
        <p className="text-sm text-emerald-700 mt-1">You'll get class updates and coach support at {status.phone}.</p>
      </div>
    );
  }

  return (
    <div className="relative rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-5" data-testid="wa-connect-card">
      {!standalone && (
        <button onClick={() => { localStorage.setItem('cmx_wa_dismiss', '1'); setDismissed(true); }}
          className="absolute top-3 right-3 p-1 rounded-full text-emerald-400 hover:bg-emerald-100" data-testid="wa-connect-dismiss">
          <X size={15} />
        </button>
      )}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0"><MessageCircle size={19} /></div>
        <div className="flex-1 min-w-0">
          <h3 className="font-[Manrope] font-bold text-slate-900">Get class reminders & coach support on WhatsApp</h3>
          <p className="text-sm text-slate-600 mt-0.5">Live class alerts, study tips, and a real coach one message away.</p>

          {!link ? (
            <>
              <button onClick={start} disabled={starting}
                className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 transition disabled:opacity-50"
                data-testid="wa-connect-btn">
                {starting ? <Loader2 size={15} className="animate-spin" /> : <MessageCircle size={15} />} Connect WhatsApp
              </button>
              <p className="text-[11px] text-slate-400 mt-2" data-testid="wa-consent-text">{status?.consent_text || 'By connecting, you agree to receive CELPIP prep tips, class invites and offers from CELPIPMax on WhatsApp. Reply STOP anytime.'}</p>
            </>
          ) : (
            <div className="mt-3 space-y-2.5">
              <p className="text-sm text-slate-700">Your verification code:</p>
              <div className="flex items-center gap-2">
                <code className="px-3 py-1.5 rounded-lg bg-white border border-emerald-200 font-mono font-bold text-emerald-700 text-lg tracking-wide" data-testid="wa-connect-code">{link.code}</code>
                <button onClick={() => { navigator.clipboard?.writeText(link.code); toast.success('Code copied'); }}
                  className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-100" data-testid="wa-copy-code"><Copy size={15} /></button>
              </div>
              {link.wa_link ? (
                <a href={link.wa_link} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 transition"
                  data-testid="wa-open-whatsapp-btn">
                  <MessageCircle size={15} /> Open WhatsApp & send the code
                </a>
              ) : (
                <p className="text-xs text-slate-500">Send this code as a WhatsApp message to the CELPIPMax number to finish connecting.</p>
              )}
              <p className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="wa-waiting-label">
                <Loader2 size={12} className="animate-spin" /> Waiting for your message… this updates automatically.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppConnectCard;
